import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  computed,
  effect,
  EventEmitter,
  HostBinding,
  inject,
  Injector,
  input,
  Input,
  model,
  OnInit,
  Optional,
  Output,
  signal,
  Type,
  ViewEncapsulation,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlContainer, FormControl, FormsModule, NgForm, NgModelGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import 'froala-editor/js/plugins.pkgd.min.js';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import FroalaEditor, { FroalaOptions } from 'froala-editor';
import { catchError, of } from 'rxjs';
import { GuidelineService, InternalLinkSearchResult, LinksService, MessagesService, ReferenceHttpService } from 'shared/domain';
import { PopoverService } from 'shared/ui-common';
import { TreetopPropertiesProvider } from 'shared/util-common';

import { NODE_DETAIL_POPOVER_REFERENCES_DATA, POPOVER_DATA, Reference, ReferenceWithNumber } from '@treetop/shared/model';
import { GuidelineElementSection, GuidelineElementType, ReferenceNumberingService } from '@treetop/shared/reference-numbering';
import { AnchorLinksSearchPopoverComponent } from '@treetop/shared/tt-anchor-link-search';

import { FroalaConfigService, FroalaToolbarType } from './froala-config.service';
import { ADD_ANCHOR_LINK_BUTTON_PREFIX, ADD_ANCHOR_LINK_CONTAINER_PREFIX } from './plugins/anchor-link.froala.plugin';
import { ADD_REFERENCE_BUTTON_PREFIX, ADD_REFERENCE_CONTAINER_PREFIX } from './plugins/references.froala.plugin';
import {
  cleanClipboardContent,
  disablePlainCopyOnKeyup,
  getAllReferenceIdsFromText,
  handleKeyDown,
  placePopupBottomLeftOfEditor,
  updateReferenceNumbersInText,
} from './rich-text-editor.util';
// eslint-disable-next-line
import type { ReferencePopoverComponent as ReferencePopoverComponentType } from '../../../feature-reference-popover/src/lib/reference-popover/reference-popover.component';
type InitControls = {
  initialize: () => void;
  destroy: () => void;
  getEditor: () => FroalaEditor;
};

/**
 * Represents the data emitted by the rich text editor component.
 * This interface is used to encapsulate information about the editor's content,
 * its associated metadata, and references.
 */
export interface RichTextEmittedData {
  text: string;
  id?: string;
  elementSection?: GuidelineElementSection;
  elementType?: GuidelineElementType;
  referenceIds?: string[];
  references?: Reference[];
  uniqueId?: string;
}

@Component({
  selector: 'common-rich-text-editor',
  standalone: true,
  imports: [FroalaEditorModule, FroalaViewModule, FormsModule, ReactiveFormsModule],
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (ngForm: NgForm, ngModelGroup: NgModelGroup) => ngModelGroup || ngForm || null,
      deps: [
        [new Optional(), NgForm],
        [new Optional(), NgModelGroup],
      ],
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class RichTextEditorComponent implements OnInit {
  private referenceNumberingService = inject(ReferenceNumberingService, { optional: true });
  private appRef = inject(ApplicationRef);
  private popoverService = inject(PopoverService);
  private linksService = inject(LinksService, { optional: true });
  private guidelineService = inject(GuidelineService, { optional: true });
  private referenceHttpService = inject(ReferenceHttpService);
  private messageService = inject(MessagesService);
  private resolver = inject(ComponentFactoryResolver);
  private injector = inject(Injector);
  private froalaConfigService = inject(FroalaConfigService);
  private translocoService = inject(TranslocoService);
  private nodeDetailPopoverReferencesComponent = inject<Type<unknown>>(NODE_DETAIL_POPOVER_REFERENCES_DATA, { optional: true });

  public editorContent = model<string>();

  public references = model<Reference[]>();

  @Input() public toolbarType: FroalaToolbarType = 'normal';
  @Input() public placeholder?: string;
  @Input() public focusOnInit?: boolean;
  @Input() public disableImageUpload? = false;
  @Input() public enableReferenceButton? = false;
  @Input() public hideAnchorLinkButton = false;
  @Input() public allowReferenceRemovalHandling = false;
  @Input() public placePopupsToBottomLeft = false;
  public displayTextWithoutColor = input(false);
  public disabled = input(false);

  @Output() public editorBlur = new EventEmitter();
  @Output() public initialized = new EventEmitter<boolean>();

  @HostBinding('class')
  public hostClass = 'tt-common-rich-text-editor';

  public initControls: InitControls;

  protected editorContentForm = new FormControl('');
  protected options: Partial<FroalaOptions>;
  protected link: InternalLinkSearchResult;

  private numberedReferences = computed(() => {
    const orderedReferencesWithNumber = this.referenceNumberingService?.orderedReferences() ?? [];
    const editorContent = this.editorContent();

    const referenceIds = new Set(getAllReferenceIdsFromText(editorContent));

    return orderedReferencesWithNumber.filter((ref) => referenceIds.has(ref.id));
  });

  private referenceIdsInContent = computed(() => {
    return [...new Set(getAllReferenceIdsFromText(this.editorContent()))];
  });

  protected _initialized = signal(false);
  protected showEditor = signal(true);

  constructor() {
    effect(() => {
      const editor = this.initControls?.getEditor();
      const numberedReferences = this.numberedReferences();

      if (this.enableReferenceButton) {
        editor?.References?.updateReferenceNumbers(numberedReferences);
      }
    });

    effect(() => {
      const references = this.references() ?? [];
      const referenceIdsInContent = this.referenceIdsInContent() ?? [];

      const filteredReferences = referenceIdsInContent.map((id) => references.find((ref) => ref.id === id)).filter(Boolean);

      if (filteredReferences.length !== references.length) {
        this.references.set(filteredReferences);
      }
    });

    effect(() => {
      const editorContent = this.editorContent();
      const referencesWithNumber = this.numberedReferences();

      if (referencesWithNumber.length === 0) {
        return;
      }

      if (!this.initControls?.getEditor().References) {
        this.initControls?.getEditor().html.set(updateReferenceNumbersInText(editorContent, referencesWithNumber));
      }
    });

    effect(() => {
      const disabled = this.disabled();
      if (disabled) {
        this.initControls?.getEditor().edit.off();
      } else {
        this.initControls?.getEditor().edit.on();
      }
    });

    // Needed because of this issue https://github.com/froala/angular-froala-wysiwyg/issues/554
    // If the editor is not initialized and the content changes, the editor goes in a broken state
    // So we hide the editor and show it again to force re-initialization
    toObservable(this.editorContent).subscribe(() => {
      if (!this._initialized()) {
        this.showEditor.set(false);
        setTimeout(() => this.showEditor.set(true), 0);
      }
    });
  }

  public ngOnInit(): void {
    this.initializeOptions();

    this.editorContent.update((content) => {
      return updateReferenceNumbersInText(content, this.numberedReferences());
    });
  }

  protected initialize(initControls: InitControls): void {
    initControls.initialize();
    this.initControls = initControls;
  }

  public blur() {
    this.cleanupImageCaption();

    const editor = this.initControls?.getEditor();

    if (editor) {
      editor.events.blurActive();
      editor.selection.clear();
      editor.cursor.del();
      editor.toolbar.hide();
    }

    this.editorBlur.emit();
  }

  private initializeOptions(): void {
    const placeholder = this.placeholder ?? this.translocoService.translate('GuidelinesSection.Main.DescriptionPlaceholder');

    const _this = this;

    this.options = this.froalaConfigService.getEditorConfigFroala(
      `${TreetopPropertiesProvider.properties.baseUrl}api/attachments/unmanaged`,
      this.placeholder || placeholder,
      this.translocoService,
    );

    const disabledTools: string[] = [];
    !this.enableReferenceButton && disabledTools.push(ADD_REFERENCE_BUTTON_PREFIX);
    this.disableImageUpload && disabledTools.push('insertImage', 'insertVideo');
    this.hideAnchorLinkButton && disabledTools.push(ADD_ANCHOR_LINK_BUTTON_PREFIX);

    this.options.toolbarButtons = this.froalaConfigService.getToolbar(this.toolbarType, {
      disabledTools,
    });

    this.options.events = {
      ...this.options.events,

      initialized: function () {
        const editor = this;
        _this._initialized.set(true);

        if (_this.disabled()) {
          editor.edit.off();
        } else {
          editor.edit.on();
        }

        editor.events.on('popups.show.link.edit', () => {
          const $popup = editor.popups.get('link.edit');
          if (!$popup || !$popup.length) return;

          const link = editor.link.get();
          if (!link) return;

          const isAnchorLink = link.hasAttribute('data-element-id');

          $popup.find('.fr-command[data-cmd]').each((_: number, btn: Element) => {
            const command = btn.getAttribute('data-cmd');

            let keepVisible = true;

            if (isAnchorLink) {
              keepVisible = command === 'linkOpen' || command === 'anchorLinkEdit';
            } else {
              keepVisible = command !== 'anchorLinkEdit';
            }

            (btn as HTMLElement).style.display = keepVisible ? '' : 'none';
          });

          $popup.find('.fr-separator').each((_: number, separator: Element) => {
            (separator as HTMLElement).style.display = isAnchorLink ? 'none' : '';
          });
        });

        editor.$el.on('click', '[data-tt-custom-element][data-reference-id]', function () {
          _this.openReferenceListPopover(
            _this.numberedReferences()?.filter((ref) => ref.id === this.getAttribute('data-reference-id')),
            this,
          );
        });

        editor.$tb.on('click', '.fr-command[data-cmd="insertLink"]', function () {
          editor.popups?.refresh('insertLink');
        });

        if (!_this.focusOnInit) {
          editor.toolbar.hide();
        }

        editor.References?.init(_this.references());
        editor.References?.updateReferenceNumbers(_this.numberedReferences());
      },
      'paste.afterCleanup': (clipboard_html: string) => cleanClipboardContent(clipboard_html, this.options),
      'html.set': function () {
        _this.initialized.emit(true);
      },

      blur: () => {
        this.blur();
      },

      keydown: function (e: KeyboardEvent) {
        handleKeyDown(e, _this.options);
      },

      keyup: function (e: KeyboardEvent) {
        disablePlainCopyOnKeyup(e, _this.options);
        if (e.key === 'Backspace' || e.key === 'Delete') {
          _this.references.set(this.References.syncReferences());
        }
      },

      'popups.show.link.insert': function () {
        if (!_this.placePopupsToBottomLeft) return;
        requestAnimationFrame(() => {
          placePopupBottomLeftOfEditor(this, 'link.insert', 'insertLink');
        });
      },

      'popups.show.ttAnchorLinkPlugin.popup': function () {
        if (!_this.placePopupsToBottomLeft) return;
        requestAnimationFrame(() => {
          placePopupBottomLeftOfEditor(this, 'ttAnchorLinkPlugin.popup', 'add-anchor-link-button');
        });
      },

      'popups.show.table.insert': function () {
        if (!_this.placePopupsToBottomLeft) return;
        requestAnimationFrame(() => {
          placePopupBottomLeftOfEditor(this, 'table.insert', 'insertTable');
        });
      },

      'commands.before': function (command: 'add-reference-button' | string) {
        if (command === 'add-reference-button') {
          this.opts._pendingAttachReferencesPopup = _this.attachReferencePopoverAngularComponent.bind(_this);
        } else if (command === ADD_ANCHOR_LINK_BUTTON_PREFIX) {
          this.opts._pendingAttachAnchorLinkPopup = _this.attachAnchorLinkPopoverAngularComponent.bind(_this);
        } else if (command === 'linkOpen') {
          const link = this.link.get() as HTMLElement;
          const hasElementId = link?.hasAttribute('data-element-id');
          if (!hasElementId) {
            return;
          }

          _this.linksService?.openAnchorLinkFromTextEditor(link);
        }
      },

      'commands.undo': function () {
        _this.references.set(this.References.syncReferences());
      },

      'commands.redo': function () {
        _this.references.set(this.References.syncReferences());
      },
    };
  }

  //fix for image caption, if there is an empty line after caption remove it
  private cleanupImageCaption() {
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.editorContentForm.value, 'text/html');
    const frInnerElements = doc.querySelectorAll('.fr-img-wrap .fr-inner');

    if (frInnerElements.length > 0) {
      frInnerElements.forEach((element) => {
        let brCount = 0;
        const childNodes = Array.from(element.childNodes);
        childNodes.forEach((child) => {
          if (child.nodeName === 'BR') {
            brCount++;
            if (brCount > 1) {
              element.removeChild(child);
            }
          }
        });
      });

      this.editorContent.set(doc.body.innerHTML);
    }
  }

  private openReferenceListPopover(references: ReferenceWithNumber[], target: HTMLElement) {
    if (!this.nodeDetailPopoverReferencesComponent) return;

    this.popoverService.open<{ references: ReferenceWithNumber[] }>(this.nodeDetailPopoverReferencesComponent, target as HTMLElement, {
      data: {
        references,
      },
      width: 400,
    });
  }

  private attachAnchorLinkPopoverAngularComponent(editor: FroalaEditor) {
    const container = document.getElementById(ADD_ANCHOR_LINK_CONTAINER_PREFIX);
    if (!container) {
      return;
    }

    const customInjector = Injector.create({
      providers: [
        {
          provide: POPOVER_DATA,
          useValue: {
            guideline: this.guidelineService?.guideline ?? { chapters: [] },
          },
        },
      ],
      parent: this.injector,
    });

    const componentRef = this.resolver.resolveComponentFactory(AnchorLinksSearchPopoverComponent).create(customInjector);
    container.appendChild(componentRef.location.nativeElement);

    this.appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();

    componentRef.instance.linkSelected.subscribe((link: InternalLinkSearchResult) => {
      if (link) {
        editor.AnchorLink.addAnchorLink(link);

        const popupEl = editor.popups.get('ttAnchorLinkPlugin.popup');
        if (popupEl) {
          container.removeChild(componentRef.location.nativeElement);
          componentRef.destroy();
          popupEl.remove();
          delete editor.popups['ttAnchorLinkPlugin.popup'];
        }
      }
    });
  }

  private attachReferencePopoverAngularComponent(editor: FroalaEditor) {
    const container = document.getElementById(ADD_REFERENCE_CONTAINER_PREFIX);
    if (!container) {
      return;
    }

    const customInjector = Injector.create({
      providers: [
        {
          provide: POPOVER_DATA,
          useValue: {
            disableExisting: false,
          },
        },
      ],
      parent: this.injector,
    });

    import('../../../feature-reference-popover/src/lib/reference-popover/reference-popover.component').then(
      ({ ReferencePopoverComponent }) => {
        const componentRef = this.resolver
          .resolveComponentFactory<ReferencePopoverComponentType>(ReferencePopoverComponent)
          .create(customInjector);
        container.appendChild(componentRef.location.nativeElement);

        this.appRef.attachView(componentRef.hostView);
        componentRef.changeDetectorRef.detectChanges();

        componentRef.instance.referencesAdded.subscribe((references) => {
          if (!references || references.length === 0) return;

          const newReferences = references.filter((ref) => /^[0-]+$/.test(ref.id));
          const existingReferences = references.filter((ref) => !newReferences.includes(ref));

          this.referenceHttpService
            .addReferences(newReferences)
            .pipe(
              catchError(() => {
                this.messageService.showError('SaveError');
                return of([] as Reference[]);
              }),
            )
            .subscribe((result) => {
              const allReferences = [...result, ...existingReferences];

              this.references.set(editor.References.addReferences(allReferences, this.references()));

              const popupEl = editor.popups.get('ttReferencePlugin.popup');
              if (popupEl) {
                container.removeChild(componentRef.location.nativeElement);
                componentRef.destroy();
                popupEl.remove();
                delete editor.popups['ttReferencePlugin.popup'];
              }
            });
        });
      },
    );
  }
}
